import { isEmpty } from '@sgme/fp';
import { decimalRules } from 'components/common-form/builder/form/node/field/SpotAndGreeksField/SpotAndGreeks.helper';
import { FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { UnderlyingTag } from 'components/underlying-tag';
import { Fragment, JSX } from 'react';
import styled from 'styled-components';

import { displayUnderlyingCode } from '../../utils';
import type { SpotAndGreeksFieldProps } from './SpotAndGreekField.connect';

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
`;

const KeyValueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 2px 0px 2px 6px;
`;

const Divider = styled.hr`
  margin: 0px 0px 0px 10px;
  padding: 0px;
`;

export function VerticalSpotAndGreeksFieldComponent({
  value: spotAndGreeks,
  id,
  defaultUnderlyingCodeType,
  tabIndex,
}: SpotAndGreeksFieldProps): JSX.Element | null {
  if (isEmpty(spotAndGreeks)) {
    return null;
  }
  return (
    <div data-e2e={`${id}-row`} className="text-primary">
      {spotAndGreeks.map((spotAndGreek, index) =>
        spotAndGreek.underlying === undefined ? null : (
          <Fragment key={spotAndGreek.underlying.id}>
            {spotAndGreek.isForex ? (
              <FieldWrapper>
                <KeyValueWrapper className="ms-0">
                  <span>
                    <span className="pe-2">Spot</span>
                    <UnderlyingTag
                      label={displayUnderlyingCode(defaultUnderlyingCodeType, spotAndGreek.underlying)}
                      value={spotAndGreek.underlying}
                      className="align-self-start mb-2"
                      isReadOnly
                    />
                  </span>
                  <span className="fs-6" data-e2e="SpotAndGreeks-spot">
                    <FormattedNumber
                      value={spotAndGreek.spot}
                      minimumFractionDigits={decimalRules.Forex.spot.minimumFractionDigits}
                      maximumFractionDigits={decimalRules.Forex.spot.maximumFractionDigits}
                      tabIndex={tabIndex}
                    />
                  </span>
                </KeyValueWrapper>
                {spotAndGreek.fxDelta !== undefined && (
                  <KeyValueWrapper className="ms-0">
                    <span className="fs-6 fw-normal">{spotAndGreek.fxDelta.reference?.replace('(%)', '')}</span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-fxDelta">
                      <FormattedNumber
                        value={spotAndGreek.fxDelta.value}
                        minimumFractionDigits={decimalRules.Forex.fxDelta.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Forex.fxDelta.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
                {spotAndGreek.fxVega !== undefined && (
                  <KeyValueWrapper className="ms-0">
                    <span className="fs-6 font-weight-normal">{spotAndGreek.fxVega.reference?.replace('(%)', '')}</span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-fxVega">
                      <FormattedNumber
                        value={spotAndGreek.fxVega.value}
                        minimumFractionDigits={decimalRules.Forex.fxVega.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Forex.fxVega.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
              </FieldWrapper>
            ) : (
              <FieldWrapper>
                <UnderlyingTag
                  label={displayUnderlyingCode(defaultUnderlyingCodeType, spotAndGreek.underlying)}
                  value={spotAndGreek.underlying}
                  className="align-self-start mb-2"
                  isReadOnly
                />
                {spotAndGreek.spot !== undefined && (
                  <KeyValueWrapper>
                    <span className="fs-6 fw-normal">
                      <FormattedMessage id="form.SpotAndGreeks.Spot" />
                    </span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-spot">
                      <FormattedNumber
                        value={spotAndGreek.spot}
                        minimumFractionDigits={decimalRules.Equity.spot.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Equity.spot.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                    </span>
                  </KeyValueWrapper>
                )}
                {spotAndGreek.deltaPct !== undefined && (
                  <KeyValueWrapper>
                    <span className="fs-6 fw-normal">
                      <FormattedMessage id="form.SpotAndGreeks.Delta" />
                    </span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-delta">
                      <FormattedNumber
                        value={spotAndGreek.deltaPct}
                        minimumFractionDigits={decimalRules.Equity.deltaPct.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Equity.deltaPct.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
                {spotAndGreek.deltaInShare !== undefined && (
                  <KeyValueWrapper>
                    <span className="fs-6 fw-normal">
                      <FormattedMessage id="form.SpotAndGreeks.DeltaShares" />
                    </span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-delta-shares">
                      <FormattedNumber
                        value={spotAndGreek.deltaInShare}
                        minimumFractionDigits={decimalRules.Equity.deltaInShare.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Equity.deltaInShare.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                    </span>
                  </KeyValueWrapper>
                )}
                {spotAndGreek.vega !== undefined && (
                  <KeyValueWrapper>
                    <span className="fs-6 fw-normal">
                      <FormattedMessage id="form.SpotAndGreeks.Vega" />
                    </span>
                    <span className="fs-6" data-e2e="SpotAndGreeks-vega">
                      <FormattedNumber
                        value={spotAndGreek.vega}
                        minimumFractionDigits={decimalRules.Equity.vega.minimumFractionDigits}
                        maximumFractionDigits={decimalRules.Equity.vega.maximumFractionDigits}
                        tabIndex={tabIndex}
                      />
                      %
                    </span>
                  </KeyValueWrapper>
                )}
              </FieldWrapper>
            )}
            {spotAndGreeks.length - 1 !== index && <Divider />}
          </Fragment>
        ),
      )}
    </div>
  );
}
